import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import DefendantsTableDataRow from './DefendantsTableDataRow';

import { getDefendantsList } from '../../api/defendantsApi/defendantsApi';
import TableContainer from '../../components/Table/TableContainer';
import Loader from '../../components/Loader';
import { FIRM_DEFENDANTS_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, DefendantData, FilterTypes } from '../../types';
import NoDataText from '../../components/NoDataText';
import TableFilters from '../../components/TableFilters/TableFilters';
import TableActions from '../../components/Table/TableActions';
import { downloadCsvorExcel } from '../../helpers/downloadCsv';
import useSnackBar from '../../hooks/useSnackBar';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { useHasPermission } from '../../hooks/useHasPermission';

const DefendantsView = () => {
  const params = useParams();
  const firmId = localStorage.getItem('firmId');
  const caseIdSearchParam = new URLSearchParams(window.location.search).get('caseId');

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const canCreate = useHasPermission('Defendants', 'Create');

  const [defendantsList, setDefendantsList] = useState<DefendantData[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const hasParams = !!Object.entries(params).length;

  const [filters, setFilters] = useState<FilterTypes>({
    firmId: firmId || '',
    clientId: '',
    defendantId: '',
    caseId: caseIdSearchParam || '',
  });
  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    name: {
      order: '',
      fieldName: 'name',
    },
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
    updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };

  useEffect(() => {
    getDefendantsList({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setDefendantsList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, match, filters, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('/v1/defendants/export', 'POST', 'Defendants_List','csv',{
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      match: match,
      filters: filters,
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Defendants exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !defendantsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={canCreate ? '/defendants/create-defendant' : undefined}
            exportList={exportList}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            setMatch={setMatch}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              timeFilter={'Created'}
              match={match}
            />
          )}

          {!isDataLoading && defendantsList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={FIRM_DEFENDANTS_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <DefendantsTableDataRow tableData={defendantsList} />
            </TableContainer>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default DefendantsView;
